import styles from "./Calendar.module.css"
import React, { useEffect, useState } from "react";
import calendar, {
    isDate,
    isSameMonth,
    getNextMonth,
    getPreviousMonth,
    getEffectiveTodayStart,
    getCzechNowLocalDate,
    WEEK_DAYS,
    WEEK_DAYS_EN,
    CALENDAR_MONTHS,
    CALENDAR_MONTHS_EN
} from "../../utils/calendar";
import { ChevronLeft } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from "react-i18next";
import { useSchedule } from "../../context/ScheduleContext";
import { getDayOfWeek, getOddEvenWeek } from "../../utils/calendar";
import type { ScheduleEntry, SchedulePart, Weekday, MonthDay, DayString, ISODate } from "../../types/schedule.types";
/**
* Props for the Calendar component.
*/ 
type CalendarProps = {
    date: Date; // Date 
    onDateChanged: (date: Date) => void; // Date change handler
    selectedDates: string[]; // List with selected dates
    className?:string // Optional additional class
    globalDates: string[], // Globally selected dates
};
/**
* Type for the date state
*/
type DateState = {
    current: Date | null;
    month: number;
    year: number;
};
/**
* A calendar for picking dates.
*
* @param props Calendar props
* @param {Date} props.date - Date
* @param {string[]} props.selectedDates - List with selected dates
* @param {string} props.className - Optional additional class
* @param {(date: Date) => void} props.onClick - Date change handler
* @returns TSX.Element
*/
const Calendar: React.FC<CalendarProps> = ({ date, onDateChanged, selectedDates, className, globalDates }) => {
    const {schedule} = useSchedule();
    
    const { t, i18n } = useTranslation(); // Translation
    // State for current date
    const [dateState, setDateState] = useState<DateState>({
        current: new Date(),
        month: new Date().getMonth() + 1, 
        year: new Date().getFullYear(),
    });
    
    // After load create a new date state with given date
    useEffect(() => {
        addDateToState(date);
    }, []);

    const addDateToState = (date: Date) => {
        // Check if given date is a dateobject
        const isDateObject = isDate(date);
        const _date = isDateObject ? date : new Date();
        // Set the date state
        setDateState({
            current: isDateObject ? date : null,
            month: +_date.getMonth() + 1,
            year: _date.getFullYear(),
        });
    };
    const getCalendarDates = () => {
        // Get the date 
        const { current, month, year } = dateState;
        const calendarMonth = month ;
        const calendarYear = year ;
        return calendar(calendarMonth, calendarYear);
    };

    // Handle selecting and deselecting
    const selectDate = (date: Date) => (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e && e.preventDefault();
        const { current } = dateState;
        addDateToState(date);
        onDateChanged(date);
    };

    // Go to previous month
    const previousMonth = () => {
        const { month, year } = dateState;
        const previousMonth = getPreviousMonth(month, year);
        setDateState({
            month: previousMonth.month,
            year: previousMonth.year,
            current: dateState.current,
        });
    };
    // Go to next month
    const nextMonth = () => {
        const { month, year } = dateState;
        const nextMonth = getNextMonth(month, year);
        setDateState({
            month: nextMonth.month,
            year: nextMonth.year,
            current: dateState.current,
        });
    };
    // Month change handlers
    const handlePrevious = () => {
        previousMonth();
    };
    const handleNext = () => {
        nextMonth();
    };

    // Render datepicker header
    const renderMonthAndYear = () => {
        const { month, year } = dateState;
        // Get the month name
        const monthKeys = Object.keys(CALENDAR_MONTHS) as Array<keyof typeof CALENDAR_MONTHS>;
        const monthname = monthKeys[Math.max(0, Math.min(month - 1, 11))];
        return (
            <div className={styles.CalendarHeader}>
                <div className={styles.PreviousButton}
                    onClick={handlePrevious}
                    title="Previous Month"
                >
                    <ChevronLeft/>
                </div>
                <div className={styles.CalendarTitle}>
                    <p>
                        { i18n.language === "en" ? CALENDAR_MONTHS_EN[monthname] : CALENDAR_MONTHS[monthname]} {year}
                    </p>
                </div>
                <div className={styles.NextButton}
                    onClick={handleNext}
                    title="next Month"
                >
                    <ChevronRight/>
                </div>
            </div >
        );
    };

    // Render the day title
    const renderDayLabel = (day: keyof typeof WEEK_DAYS, index: number) => {
        // Resolve the day of the week label from the WEEK_DAYS object map
        let daylabel 
        if (i18n.language === "en") {
            daylabel = WEEK_DAYS_EN[day].toUpperCase();
        }else{
            daylabel = WEEK_DAYS[day].toUpperCase();
        }
        return (
            <div key={daylabel} className={styles.DayName}>
                <p>{daylabel}</p>
            </div>
        );
    };

    // Render the date button
    const renderCalendarDate = (date: (string | number)[], index: number) => {
        // Get the date state
        const { current, month, year } = dateState;
        // Convert all elements to strings before joining
        const dateParts = date.map(String);
        // Key for the element
        const dateKey = dateParts.join("-");
        // Generate new date in local time to avoid UTC offset shifts
        const [y, m, d] = dateParts.map(Number);
        const _date = new Date(y, m - 1, d);
        // Check if calendar date is in the same month as the state month and year
        const inMonth = month && year && isSameMonth(_date, new Date([year, month, 1].join("-")));
        // The click handler
        const onClick = selectDate(_date);
        // Get actual current date-time and Czech-local clock
        const nowActual: Date = new Date();
        const nowCzech: Date = getCzechNowLocalDate(nowActual);
        const effectiveTodayStart = getEffectiveTodayStart(nowActual);
        // Find if date is older than effective "today"
        const isOlder =  _date.getTime() - effectiveTodayStart.getTime()

        const pad2 = (n: number) => String(n).padStart(2, '0');

        const iso = (d: Date) => {
            const y = d.getFullYear();
            const m = pad2(d.getMonth() + 1);
            const day = pad2(d.getDate());
            return `${y}-${m}-${day}`; // local-agnostic ISO date
        };

        const monthDay = (d: Date, padded = true) => {
            const m = d.getMonth() + 1;
            const day = d.getDate();
            return padded ? `${pad2(m)}-${pad2(day)}` : `${m}-${day}`;
        };

        const dayOfMonthKey = (d: Date, padded = true) =>
        padded ? pad2(d.getDate()) : String(d.getDate());

        let blocked = false
        const scheduleReg = schedule?.regular_schedule;
        const tsWeek = getOddEvenWeek(_date); // 'odd' | 'even'
        const tsDay = getDayOfWeek(_date); // 'Thursday' etc.
        const keyNever = iso(_date); // '2025-10-31'
        const keyMonthly = dayOfMonthKey(_date, true); // '09', '31'
        const keyYearly = monthDay(_date, true); // '11-13'

        // Never — override first (or last, but format must match)
        
        if (scheduleReg?.never && keyNever in scheduleReg.never) {
            blocked = false;
        } else {
            // Blocked Weekly
            if (scheduleReg?.weekly && tsDay in scheduleReg.weekly) blocked = true;

            // Blocked Biweekly
            if (
                (scheduleReg?.biweekly?.odd && tsDay in scheduleReg.biweekly.odd && tsWeek === 'odd') ||
                (scheduleReg?.biweekly?.even && tsDay in scheduleReg.biweekly.even && tsWeek === 'even')
            ) {
                blocked = true;
            }

            // Blockec Monthly
            if (scheduleReg?.monthly && keyMonthly in scheduleReg.monthly) blocked = true;

            // Blocked Yearly
            if (scheduleReg?.yearly && keyYearly in scheduleReg.yearly) blocked = true;
        }
            


              // --- OPERATION SCHEDULE LOGIC (adjacent-day effects) ---
            const scheduleOp = schedule?.operation_schedule;

            // tiny helpers
            const isSameDay = (a: Date, b: Date) =>
                a.getFullYear() === b.getFullYear() &&
                a.getMonth() === b.getMonth() &&
                a.getDate() === b.getDate();

            // Check for same calendar date
            const has = (obj: unknown, key: PropertyKey): boolean =>
                !!obj && typeof obj === 'object' &&
                Object.prototype.hasOwnProperty.call(obj, key);

            // read any matching entry (weekly/biweekly/monthly/yearly/never)
            const getMatchingEntry = (part: SchedulePart | undefined, d: Date): ScheduleEntry | undefined => {
                if (!part) return undefined;

                const week = getOddEvenWeek(d);                 // 'odd' | 'even'
                const dow  = getDayOfWeek(d) as Weekday;        // 'Monday'...'Sunday'
                const kNever   = iso(d) as ISODate;             // '2025-10-31'
                const kMonthly = dayOfMonthKey(d, true) as DayString; // '01'..'31'
                const kYearly  = monthDay(d, true) as MonthDay; // '11-13'

                // precedence: exact date → weekly/biweekly → monthly → yearly
                if (has(part.never, kNever))   return (part.never as Record<string, ScheduleEntry>)[kNever];
                if (has(part.weekly, dow))     return (part.weekly as Record<string, ScheduleEntry>)[dow];

                if (part.biweekly) {
                    if (week === 'odd' && has(part.biweekly.odd, dow))
                        return (part.biweekly.odd as Record<string, ScheduleEntry>)[dow];
                    if (week === 'even' && has(part.biweekly.even, dow))
                        return (part.biweekly.even as Record<string, ScheduleEntry>)[dow];
                }

                if (has(part.monthly, kMonthly)) return (part.monthly as Record<string, ScheduleEntry>)[kMonthly];
                if (has(part.yearly, kYearly))   return (part.yearly  as Record<string, ScheduleEntry>)[kYearly];

                return undefined;
            };

            // a day is "non-operating" if ANY entry matches in operation_schedule (ignore flags)
            const isNonOperating = (d: Date): boolean => {
                return !!getMatchingEntry(scheduleOp, d);
            };

        // Rule:
        // - If the previous day of _date is non-operating:
        //    * If TODAY is that previous day → block _date all day.
        //    * If TODAY is the day before that previous day → block _date after 17:00.
        // --- OPERATION SCHEDULE LOGIC (adjacent-day + cascade) ---
        // --- OPERATION SCHEDULE LOGIC (adjacent-day + cascade, no side effects) ---
        (() => {
            const scheduleOp = schedule?.operation_schedule;
            if (!scheduleOp) return;

            const prev = new Date(_date);
            prev.setDate(prev.getDate() - 1);

            const isNonOperating = (d: Date): boolean => !!getMatchingEntry(scheduleOp, d);

            // quick exits when previous day isn’t non-operating
            if (!isNonOperating(prev)) return;

            const now = nowCzech;
            const cutoff = new Date(now);
            cutoff.setHours(17, 0, 0, 0);

            const dayBeforePrev = new Date(prev);
            dayBeforePrev.setDate(dayBeforePrev.getDate() - 1);

            // Base rule: Sunday non-op => Monday blocked; Saturday >=17:00 => Monday blocked
            if (isSameDay(now, prev)) {
                blocked = true;
                return;
            }
            if (isSameDay(now, dayBeforePrev) && now.getTime() >= cutoff.getTime()) {
                blocked = true;
                return;
            }

            // Cascade rule: if there is NO usable operating day in [today .. prev]  (inclusive of today)
            // then block _date. We only decide this _date — no side effects on other days.
            const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // today @ 00:00
            const end = new Date(prev.getFullYear(), prev.getMonth(), prev.getDate()); // prev @ 00:00

            let cursor = new Date(start);
            let hasUsableOperatingDay = false;

            while (cursor.getTime() <= end.getTime()) {
                const closedAllDay = isNonOperating(cursor);

                if (!closedAllDay) {
                if (isSameDay(cursor, now)) {
                    // today might be unusable if tomorrow is closed AND it’s already after 17:00
                    const tomorrow = new Date(cursor);
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    const tomorrowClosed = isNonOperating(tomorrow);
                    const afterCutoff = now.getTime() >= cutoff.getTime();
                    if (!(tomorrowClosed && afterCutoff)) {
                    hasUsableOperatingDay = true; // today is usable
                    break;
                    }
                } else {
                    // any open day after today and before _date is usable
                    hasUsableOperatingDay = true;
                    break;
                }
                }

                cursor.setDate(cursor.getDate() + 1);
            }

            if (!hasUsableOperatingDay) {
                blocked = true;
            }
        })();



        // If you are testing only operation schedule, keep this commented out:
        
        if (has(scheduleReg?.never, keyNever)) {
            blocked = false;
        }
        


        // Classes
        const classNames = [
            styles.DateItem,
            selectedDates.includes(dateKey) && styles.Selected,
            globalDates.includes(dateKey) && styles.Global,
            isOlder <= 0 && styles.OlderBlock,
            blocked && styles.Blocked,
        ].filter(Boolean).join(' ');

        return (
            <div
            className={classNames}  
            onClick={isOlder > 0 ? onClick : ()=>{}} key={dateKey}
                    > 
                <p className={`${inMonth ? styles.ThisMonth : ``} ${isOlder <= 0 ? styles.Older : ""} `}>{_date.getDate()}</p>
            </div>
        );
    }
        return(
            <>
            <div className={className ? `${styles.DatePicker} ${className}` : `${styles.DatePicker}`}>
                {renderMonthAndYear()}
                <div className={styles.DatePickerContent}>
                    {i18n.language === "en" ? (
                        <div className={styles.DayLabels}>{Object.keys(WEEK_DAYS_EN).map((day, idx) => renderDayLabel(day as keyof typeof WEEK_DAYS_EN, idx))}</div>
                    ):(
                        <div className={styles.DayLabels}>{Object.keys(WEEK_DAYS).map((day, idx) => renderDayLabel(day as keyof typeof WEEK_DAYS, idx))}</div>
                    )}
                    <div className={styles.DateList}>{getCalendarDates().map(renderCalendarDate)}</div>
                </div>
            </div>
            </>
        )
}

export default Calendar
